import '../styles/Project.css';
import '../styles/Zoom.css';
import Zoom from 'react-medium-image-zoom';
import { Icon } from '@iconify-icon/react';
import { Link } from "react-router-dom";

export default function OpportunityMarketplace() {

    return (
        <div className='content'>

            {/* ========================
                HEADER
            ========================= */}
            <div className='projectHeadline'>
                <h2 className='gradientText'>
                    Opportunity Marketplace.
                </h2>

                <p className='subtitle projectHmw'>
                    Connecting talented staff with the projects that need them
                </p>

                <div className='projectIntro'>

                    <div className='innerProjectIntro'>
                        <p className='overline'>Timeline</p>
                        <p className='small'>Feb – May 2026</p>
                    </div>

                    <div className='innerProjectIntro'>
                        <p className='overline'>Team</p>

                        <ul className='small'>
                            <li>1 Project Lead</li>
                            <li>1 UX Researcher</li>
                            <li>2 Developers</li>
                            <li>2 UX Designers (including me)</li>
                        </ul>
                    </div>

                    <div className='innerProjectIntro'>
                        <p className='overline'>My Role</p>

                        <ul className='small'>
                            <li>Product Design</li>
                            <li>UI / UX Design</li>
                            <li>Design Systems</li>
                        </ul>
                    </div>

                </div>
            </div>


            {/* ========================
                HERO
            ========================= */}

            <div className='projectOverview'>
                <img
                    src="/pics/om/OM.png"
                    alt="Opportunity Marketplace"
                />
            </div>


            {/* ========================
                OVERVIEW
            ========================= */}

            <div className='rows'>

                <div className='projectContentRow'>
                    <h4 className='gradientText'>Overview</h4>
                    <p>

                        Opportunity Marketplace is an <span style={{ color: "var(--primary)", fontWeight: 500, fontStyle: "italic" }}>internal platform that connects project leads with employees who have specialized expertise</span> across National Renewable Energy Laboratory (NREL).

                        Previous solutions—including SharePoint spreadsheets and AI chatbots—failed to gain adoption.

                        Rather than redesigning the interface alone, we set out to understand why earlier attempts failed and what would motivate both sides of the marketplace to participate.

                    </p>
                </div>


                <div className='projectContentRow'>
                    <h4 className='gradientText'>My Role</h4>

                    <p>

                        I led <span style={{ color: "var(--primary)", fontWeight: 500, fontStyle: "italic" }}>end-to-end product design</span> after the standalone concept was selected, owning user flows for both user groups,

                        lo-fi through hi-fi wireframes,

                        responsive design,

                        design system implementation,

                        and developer handoff.

                        <br /><br />

                        I also <span style={{ color: "var(--primary)", fontWeight: 500, fontStyle: "italic" }}>defined the marketplace's user-state model</span> and <span style={{ color: "var(--primary)", fontWeight: 500, fontStyle: "italic" }}>facilitated twice-weekly design workshops</span> that kept researchers,

                        developers,

                        and stakeholders aligned throughout the project.

                    </p>
                </div>


                <div className='projectContentRow'>
                    <h4 className='gradientText'>Initial Problem</h4>
                    <p>The platform only works if both sides keep their information current, so <span style={{ color: "var(--primary)", fontWeight: 500, fontStyle: "italic" }}>adoption was the design problem and the business problem</span> at once. Some sub-challenges included:
                        <br />
                        <br />
                        <ul>
                            <li>How might we encourage adoption without making the tool feel like something users need to learn?</li>
                            <li>How might we connect users’ existing Workday data without making profile upkeep feel like duplicate work?</li>
                            <li>How might we serve two distinct user groups — project leads and contributors — on one platform without either getting lost?</li>
                            <li>How might we keep profiles and projects up to date, so the marketplace stays genuinely two-sided?</li>
                        </ul>
                        <br />Alongside the design problems, the team faced a <span style={{ color: "var(--primary)", fontWeight: 500, fontStyle: "italic" }}>credibility problem: </span>leadership had funded failed attempts before. We needed to prove this approach was different before we could get the resources to build it.
                    </p>
                </div>


                <div className='projectContentRow'>
                    <h4 className='gradientText'>Results</h4>
                    <p>
                        <ul>
                            <li>Won leadership buy-in on the <span style={{ color: "var(--primary)", fontWeight: 500, fontStyle: "italic" }}>first review</span>, clearing the credibility hurdle left by previous failed attempts.</li>
                            <li><span style={{ color: "var(--primary)", fontWeight: 500, fontStyle: "italic" }}>12 of 14</span> testers agreed or strongly agreed they would be <span style={{ color: "var(--primary)", fontWeight: 500, fontStyle: "italic" }}>excited to use</span> the tool; <span style={{ color: "var(--primary)", fontWeight: 500, fontStyle: "italic" }}>13 of 14</span> agreed it seemed <span style={{ color: "var(--primary)", fontWeight: 500, fontStyle: "italic" }}>easy to learn</span> and use.</li>
                            <li>Narrowed <span style={{ color: "var(--primary)", fontWeight: 500, fontStyle: "italic" }}>4 competing concepts to 1</span> through three rounds of rapid prototyping and testing.</li>
                            <li>Delivered a <span style={{ color: "var(--primary)", fontWeight: 500, fontStyle: "italic" }}>development-ready handoff</span>: 4 complete user flows prototyped in one week, refined over 4–5 rounds, fully annotated, and handed off in two days.</li>
                        </ul>

                        <br />
                        I left the lab as the project entered development for its internal beta.
                    </p>

                </div>

            </div>


            {/* ========================
                PRODUCT IMAGE
            ========================= */}

            <div className='projectImg'>

                <img
                    src="/pics/om/OM_Progress.png"
                    alt="Lo-fi to hi-fi iteration"
                />

            </div>


            {/* ========================
                PRODUCT VOICE
            ========================= */}

            <div className='projectContent'>

                <h4 className='gradientText'>
                    The finding that changed the product's voice
                </h4>

                <p>

                    One of our business goals was high early adoption, so the first designs used enthusiastic language: “find the work you love,” “exciting work,” “excited to contribute.”

                    <br /><br />

                    User research showed the opposite of what we expected.
                    Participants described the language as promotional rather than helpful.

                    <br /><br />

                    We replaced aspirational messaging with practical language—for example,
                    "Find the work you love"
                    became
                    "Find opportunities that fit your skills."

                </p>

                <div className='projectImg'>

                    <Zoom>

                        <img
                            src="/pics/om/OM_Language.png"
                            alt="Language iteration"
                        />

                    </Zoom>

                </div>

            </div>


            {/* ========================
                MARKETPLACE MECHANICS
            ========================= */}

            <div className='projectContent'>

                <h4 className='gradientText'>
                    Designing the marketplace mechanics
                </h4>

                <p>
                    A marketplace only works if both sides show up. I defined four user states and the rules governing each:
                    <br />
                    <br />
                    <ul>
                        <li><span style={{ color: "var(--primary)", fontWeight: 500 }}>Inactive contributor:</span> can browse opportunities, but can’t apply or appear in project leads’ searches. Status resets every 180 days unless set to “Open for opportunities.”</li>
                        <li><span style={{ color: "var(--primary)", fontWeight: 500 }}>Active contributor:</span> full access.</li>
                        <li><span style={{ color: "var(--primary)", fontWeight: 500 }}>Inactive project lead:</span> can browse contributors, but can’t see names or photos, or reach out, until a project is posted.</li>
                        <li><span style={{ color: "var(--primary)", fontWeight: 500 }}>Active project lead:</span> full access.</li>
                    </ul>

                    <br /><br />

                    Rather than acting as restrictions, these rules encourage users to keep their availability and project information up to date,
                    ensuring the marketplace remains useful over time.

                </p>

                <div className='projectImg'>

                    <Zoom>

                        <img
                            src="/pics/om/OM_Inactive&Active.png"
                            alt="Marketplace mechanics"
                        />

                    </Zoom>

                </div>

            </div>


            {/* ========================
                RESEARCH DECISIONS
            ========================= */}

            <div className='projectContent'>

                <h4 className='gradientText'>
                    Research that changed the design
                </h4>

            </div>

            {/* Feature 1 */}

            <div className='feature'>

                <div className='projectImg' id='omImg'>
                    <Zoom>
                        <img src="/pics/om/OM_Dupwork.png" alt="Duplicate work improvement" />
                    </Zoom>
                </div>

                <div className='featureContent' id="omFeatureContent">

                    <h5 className='gradientText'>
                        Eliminating duplicate work
                    </h5>

                    <p>

                        <span style={{ color: "var(--primary)", fontWeight: 500 }}>Finding</span>
                        <br />
                        Users worried about maintaining profiles in multiple places.
                        <br /><br />

                        <span style={{ color: "var(--primary)", fontWeight: 500 }}>Design</span>
                        <br />
                        Linked profiles directly with Workday so users only update information once. Added clear directions on how to update profiles in Workday.

                        <br /><br />

                        <span style={{ color: "var(--primary)", fontWeight: 500 }}>Impact</span>
                        <br />
                        Participants said duplicate maintenance was no longer a concern.

                    </p>
                </div>
            </div>

            {/* Feature 2 */}
            <div className='feature'>

                <div className='featureContent' id="omFeatureContent">

                    <h5 className='gradientText'>
                        Positive language backfired
                    </h5>
                    <p>

                        <span style={{ color: "var(--primary)", fontWeight: 500 }}>Finding</span>
                        <br />
                        Users reacted negatively to overly positive framing.
                        <br /><br />

                        <span style={{ color: "var(--primary)", fontWeight: 500 }}>Design</span>
                        <br />
                        Switched to plain, practical language.

                        <br /><br />

                        <span style={{ color: "var(--primary)", fontWeight: 500 }}>Result</span>
                        <br />
                        12 of 14 participants agreed or strongly agreed they would be excited to use the tool.

                    </p>
                </div>


                <div className='projectImg' id='omImg'>
                    <Zoom>
                        <img src="/pics/om/OM_Dupwork.png" alt="Positive language improvement" />
                    </Zoom>
                </div>
            </div>

            {/* Feature 3 */}

            <div className='feature'>

                <div className='projectImg' id='omImg'>
                    <Zoom>
                        <img src="/pics/om/OM_Filters.png" alt="Filters improvement" />
                    </Zoom>
                </div>

                <div className='featureContent' id="omFeatureContent">

                    <h5 className='gradientText'>
                        Improving filter placement and options
                    </h5>

                    <p>

                        <span style={{ color: "var(--primary)", fontWeight: 500 }}>Finding</span>
                        <br />
                        Filters sat above the search button, implying users should configure before searching. Users wanted to browse and filter simultaneously. Testers also rated filters individually — showing some filters are not helpful or as important.
                        <br /><br />

                        <span style={{ color: "var(--primary)", fontWeight: 500 }}>Design</span>
                        <br />
                        Moved filters to a left rail alongside live results. Removed low-value filters, added highly-requested ones, and reordered the rest by importance.

                        <br /><br />

                        <span style={{ color: "var(--primary)", fontWeight: 500 }}>Impact</span>
                        <br />
                        Participants said the improved filtering experience was a significant enhancement.

                    </p>
                </div>
            </div>


            <div className='projectContent'>

                <h4 className='gradientText'>
                    Takeaways
                </h4>

                <div className='bulletPoints'>

                    <p className='subtitle gradientText'>
                        Invest in what matters most
                    </p>

                    <p>
                        Spending more time on the highest-impact workflows created a stronger overall experience than attempting to perfect every screen.
                    </p>

                </div>

                <div className='bulletPoints'>

                    <p className='subtitle gradientText'>
                        Data resolves design debates
                    </p>

                    <p>
                        Rather than relying on opinions, user research and rapid prototypes provided evidence that aligned stakeholders around the best direction.
                    </p>

                </div>

                <div className='bulletPoints'>

                    <p className='subtitle gradientText'>
                        Constraints can become incentives
                    </p>

                    <p>
                        The same rules that limited inactive users also encouraged continuous participation, helping the marketplace sustain itself over time.
                    </p>

                </div>

            </div>


            {/* ========================
                PREV NEXT
            ========================= */}

            <div className='prevNext'>
                <Link className='subtitle' role="button" to="/"><Icon icon='icon-park-outline:left' style={{ fontSize: '26px' }} />See other projects</Link>
                
            </div>

        </div>
    );

}